/**
 * HALO gstate path map + console state builders (Phase 2).
 * Writes are gated by EdgeDevice attrs.readOnlyMode (default off).
 */

/** Device-local paths (hit via Virtual Edge http_request). */
export const HALO_PATHS = {
	versions: '/api/config/gstate/versions',
	config: '/api/config/gstate/config',
	ident: '/api/config/gstate/config/ident',
	vms: '/api/config/gstate/config/vms',
	vms2: '/api/config/gstate/config/vms2',
	heartbeat: '/api/config/gstate/config/heartbeat',
	bacnet: '/api/config/gstate/config/bacnet',
	snmp: '/api/config/gstate/config/snmp',
	api: '/api/config/gstate/config/api',
	eventgen: '/api/config/gstate/config/eventgen',
	htsensor: '/api/config/gstate/config/sensor_config/htsensor',
	mics: '/api/config/gstate/config/sensor_config/mics',
	drivers: '/api/config/gstate/config/drivers',
	blebcn: '/api/config/gstate/config/drivers/blebcn/config',
	latest: '/api/config/gstate/latest',
	cloud: '/api/config/gstate/cloud',
	https: '/api/config/gstate/https',
	eventState: '/api/config/gstate/event_state',
	timeok: '/api/config/gstate/timeok',
	force: '/api/config/gstate/force',
	presets: '/api/config/presets',
	verification: '/api/config/gstate/config/api/verification',
	configReset: '/api/config?default=1',
};

/** Spec default preset names when the device list is empty. */
export const HALO_DEFAULT_PRESETS = [
	'Vaping',
	'Smoking',
	'Security',
	'Environmental - Basic',
	'Environmental - Advanced',
];

/** Apply-mode options for preset apply. */
export const PRESET_APPLY_MODES = ['Overwrite', 'Merge', 'Keep'];

/** Integration auth mode options. */
export const AUTH_MODE_OPTIONS = ['Basic', 'Bearer', 'OAuth2'];

/**
 * Whether Halo console writes are blocked for this EdgeDevice (attrs.readOnlyMode).
 * Default is off.
 * @param {Object} edgeDevice
 * @returns {boolean}
 */
export function isHaloReadOnlyMode(edgeDevice) {
	if (!edgeDevice || typeof edgeDevice !== 'object') return false;
	if (edgeDevice.readOnlyMode === true) return true;
	const attrs = edgeDevice.attrs || {};
	return attrs.readOnlyMode === true;
}

/**
 * Build timezone / NTP mutator path (GET that sets OS time).
 * @param {{ tz?: string, ntp?: boolean, srv?: string }} opts
 * @returns {string}
 */
export function settimeinfoPath(opts = {}) {
	const q = new URLSearchParams({
		tz: opts.tz || '',
		ntp: opts.ntp ? 'yes' : 'no',
		srv: opts.srv || '',
	});
	return `/api/device/settimeinfo?${q}`;
}

/**
 * Build device exec path (reboot / shutdown).
 * @param {string} command
 * @param {string} verify
 * @returns {string}
 */
export function execPath(command, verify) {
	const q = new URLSearchParams({
		command: command || 'reboot',
		verify: verify || '',
	});
	return `/api/device/exec?${q}`;
}

/**
 * Pull the verification key from a gstate/api/verification GET body.
 * @param {*} body
 * @returns {string}
 */
export function parseVerificationKey(body) {
	if (typeof body === 'string' && body.trim()) return body.trim();
	if (body && typeof body === 'object' && typeof body.verification === 'string') {
		return body.verification.trim();
	}
	return '';
}

/**
 * Empty console shell used before/while live data loads.
 * @param {Object} edgeDevice
 * @returns {Object}
 */
export function emptyConsoleState(edgeDevice = {}) {
	const name = edgeDevice.name && edgeDevice.name !== '—'
		? edgeDevice.name
		: (edgeDevice.key || 'HALO');
	return {
		tenant: '—',
		online: edgeDevice.status === 'online',
		fleet: [],
		device: {
			id: edgeDevice.id || edgeDevice.key || null,
			name,
			shortName: name,
			model: '—',
			location: '—',
			firmware: '—',
			ip: (edgeDevice.attrs && edgeDevice.attrs.host) || '—',
			mac: '—',
			serial: '—',
			uptime: '—',
			subnet: '—',
			gateway: '',
			link: '—',
			assignment: 'Unknown',
			timezone: '',
			ntpServer: '',
			ntpEnabled: true,
			clockInSync: false,
		},
		health: {
			cloud: { status: 'warn', title: 'Loading…', detail: '' },
			heartbeat: { status: 'warn', title: 'Loading…', detail: '' },
			primary: { status: 'warn', title: 'Loading…', detail: '' },
			events: { status: 'warn', title: 'Loading…', detail: '' },
		},
		readings: [],
		identity: emptyIdentity(),
		presets: {
			names: [...HALO_DEFAULT_PRESETS],
			selected: '',
			mode: 'Overwrite',
		},
		integrations: {
			activeTab: 'heartbeat',
			primary: emptyIntegration('primary'),
			secondary: emptyIntegration('secondary'),
			heartbeat: emptyIntegration('heartbeat'),
		},
		events: { configured: 0, enabled: 0, groups: [], raw: null },
		sensors: {
			tempOffsetC: '0',
			volumeDb: '0',
			micLevelL: '0',
			micLevelR: '0',
			micGainL: '0',
			micGainR: '0',
			micDbOffset: '0',
			amplifyEnabled: false,
			amplifyNote: '',
		},
		advanced: {
			bacnet: emptyBacnet(),
			snmp: emptySnmp(),
			web: emptyWeb(),
		},
		protocolOptions: ['HTTP', 'TCP', 'HTTPS'],
		authModeOptions: [...AUTH_MODE_OPTIONS],
		presetModeOptions: [...PRESET_APPLY_MODES],
		assignmentOptions: ['Unknown', 'DHCP', 'Static'],
		_raw: {},
	};
}

/**
 * @returns {Object}
 */
function emptyIdentity() {
	return {
		name: '',
		wing: '',
		floor: '',
		room: '',
		st_addr: '',
		desc: '',
		latitude: '',
		longitude: '',
		student_count: '',
		_raw: null,
	};
}

/**
 * @returns {Object}
 */
function emptyBacnet() {
	return {
		instance: '',
		port: '',
		deviceName: '',
		covIncrement: '',
		location: '',
		foreignDevice: false,
		foreignBBMD: '',
		foreignTTL: '30',
		_raw: null,
	};
}

/**
 * @returns {Object}
 */
function emptySnmp() {
	return {
		enabled: false,
		community: '',
		username: '',
		rocommunity: '',
		rousername: '',
		location: '',
		contact: '',
		_raw: null,
	};
}

/**
 * @returns {Object}
 */
function emptyWeb() {
	return {
		httpsPort: '',
		realm: '',
		redirectHttp: false,
		_raw: null,
	};
}

/**
 * @param {string} kind
 * @returns {Object}
 */
function emptyIntegration(kind) {
	return {
		enabled: false,
		status: 'off',
		statusLabel: 'Disabled',
		host: '',
		port: '',
		endpoint: kind === 'heartbeat' ? '' : '',
		protocol: 'HTTP',
		retryHoldoff: '5',
		interval: kind === 'heartbeat' ? '60' : null,
		authMode: 'Basic',
		username: '',
		password: '',
		bearerToken: '',
		bearerAuthUrl: '',
		oauthAuthUrl: '',
		refreshToken: '',
		refreshTokenKey: '',
		error: null,
		_raw: null,
	};
}

/**
 * Infer auth mode from a VMS / heartbeat raw object.
 * @param {Object} r
 * @returns {'Basic'|'Bearer'|'OAuth2'}
 */
function detectAuthMode(r) {
	const bd = String((r && r.basic_digest) || '').toUpperCase();
	if (bd.includes('OAUTH') || (r && (r.auth_url || r.refresh_token))) return 'OAuth2';
	if (bd.includes('BEARER') || (r && (r.bearerlogintokeno || r.bearerauthurl))) return 'Bearer';
	return 'Basic';
}

/**
 * Map a VMS / heartbeat gstate object into the Integrations form model.
 * @param {Object} raw
 * @param {'primary'|'secondary'|'heartbeat'} kind
 * @param {Object} [httpsState]
 * @returns {Object}
 */
export function mapIntegration(raw, kind, httpsState = {}) {
	const r = raw || {};
	const enabled = kind === 'heartbeat'
		? !!r.on
		: !!(r.address && String(r.address).length && r.address !== '0.0.0.0');
	const endpoint = kind === 'heartbeat'
		? String(r.msg || '')
		: String((r.set && r.set.string) || '');
	let status = 'off';
	let statusLabel = 'Disabled';
	let error = null;
	const stateKey = kind === 'primary' ? 'vmsstate' : (kind === 'secondary' ? 'vmsstate2' : 'heartbeatstate');
	const st = httpsState && httpsState[stateKey];
	if (enabled) {
		if (st && st !== '-' && /error|fail|unavail|404|503|500/i.test(String(st))) {
			status = 'error';
			statusLabel = 'Error';
			error = String(st);
		} else if (st && st !== '-') {
			status = 'ok';
			statusLabel = String(st);
		} else {
			status = 'warn';
			statusLabel = 'Enabled';
		}
	}
	return {
		enabled,
		status,
		statusLabel,
		host: String(r.address || ''),
		port: r.port != null ? String(r.port) : '',
		endpoint,
		protocol: String(r.tcp || 'HTTP'),
		retryHoldoff: r.delay != null ? String(r.delay) : '5',
		interval: r.seconds != null ? String(r.seconds) : (kind === 'heartbeat' ? '60' : null),
		authMode: detectAuthMode(r),
		username: String(r.user || ''),
		password: r.pswdo ? String(r.pswdo) : '',
		bearerToken: String(r.bearerlogintokeno || ''),
		bearerAuthUrl: String(r.bearerauthurl || ''),
		oauthAuthUrl: String(r.auth_url || ''),
		refreshToken: String(r.refresh_token || ''),
		refreshTokenKey: String(r.refresh_token_key || ''),
		error,
		_raw: r,
		_kind: kind,
	};
}

/**
 * Build PUT body for an integration channel from the form model.
 * @param {Object} form
 * @param {'primary'|'secondary'|'heartbeat'} kind
 * @returns {Object}
 */
export function integrationPutBody(form, kind) {
	const raw = { ...(form._raw || {}) };
	raw.address = form.host;
	raw.port = Number(form.port) || 0;
	raw.tcp = form.protocol || 'HTTP';
	const mode = form.authMode || 'Basic';
	if (mode === 'Bearer') {
		raw.basic_digest = 'BEARER';
		raw.bearerlogintokeno = form.bearerToken || '';
		raw.bearerauthurl = form.bearerAuthUrl || '';
	} else if (mode === 'OAuth2') {
		raw.basic_digest = 'OAUTH2';
		raw.auth_url = form.oauthAuthUrl || '';
		raw.refresh_token = form.refreshToken || '';
		raw.refresh_token_key = form.refreshTokenKey || '';
	} else {
		raw.basic_digest = 'BASICDIGEST';
		raw.user = form.username || '';
		if (form.password && form.password !== '••••••••') {
			raw.pswdo = form.password;
		}
	}
	if (kind === 'heartbeat') {
		raw.on = !!form.enabled;
		raw.msg = form.endpoint || '';
		raw.seconds = Number(form.interval) || 60;
		raw.delay = Number(form.retryHoldoff) || raw.delay || 5;
	} else {
		raw.delay = Number(form.retryHoldoff) || 5;
		if (!raw.set) raw.set = { string: '', send: false };
		if (!raw.reset) raw.reset = { string: '', send: false };
		raw.set = { ...raw.set, string: form.endpoint || raw.set.string || '' };
		if (!form.enabled) {
			raw.address = raw.address || '0.0.0.0';
		}
	}
	return raw;
}

/**
 * Normalize preset list response from the device.
 * @param {*} body
 * @returns {string[]}
 */
export function mapPresets(body) {
	if (Array.isArray(body)) return body.map(String).filter(Boolean);
	if (body && Array.isArray(body.presets)) return body.presets.map(String).filter(Boolean);
	if (body && Array.isArray(body.names)) return body.names.map(String).filter(Boolean);
	if (body && typeof body === 'object') {
		const keys = Object.keys(body).filter((k) => !k.startsWith('_') && k !== '$info$');
		if (keys.length) return keys;
	}
	return [...HALO_DEFAULT_PRESETS];
}

/**
 * Build path to apply a named preset.
 * @param {string} name
 * @param {string} [mode]
 * @returns {string}
 */
export function presetApplyPath(name, mode = 'Overwrite') {
	const q = mode ? `?mode=${encodeURIComponent(String(mode).toLowerCase())}` : '';
	return `${HALO_PATHS.presets}/${encodeURIComponent(name)}${q}`;
}

/**
 * Format location line from ident fields.
 * @param {Object} ident
 * @returns {string}
 */
export function formatIdentLocation(ident) {
	const id = ident || {};
	const parts = [id.wing, id.floor && `Floor ${id.floor}`, id.room || id.desc].filter(Boolean);
	if (parts.length) return parts.join(' · ');
	return id.st_addr || '—';
}

/**
 * @param {Object} form identity UI model
 * @returns {Object} PUT body for /config/ident
 */
export function identPutBody(form) {
	const raw = { ...(form._raw || {}) };
	return {
		...raw,
		wing: form.wing || '',
		floor: form.floor || '',
		room: form.room || '',
		st_addr: form.st_addr || '',
		desc: form.desc || '',
		latitude: form.latitude || '',
		longitude: form.longitude || '',
		student_count: form.student_count || '',
	};
}

/**
 * @param {Object} form
 * @returns {Object}
 */
export function bacnetPutBody(form) {
	const raw = { ...(form._raw || {}) };
	return {
		...raw,
		instanceNumber: Number(form.instance),
		port: Number(form.port),
		deviceName: form.deviceName || '',
		covIncrement: Number(form.covIncrement),
		location: form.location || '',
		foreignDevice: !!form.foreignDevice,
		foreignBBMD: form.foreignBBMD || '',
		foreignTTL: Number(form.foreignTTL) || 30,
	};
}

/**
 * @param {Object} form
 * @returns {Object}
 */
export function snmpPutBody(form) {
	const raw = { ...(form._raw || {}) };
	return {
		...raw,
		on: !!form.enabled,
		ena: !!form.enabled,
		community: form.community || '',
		username: form.username || '',
		rocommunity: form.rocommunity || '',
		rousername: form.rousername || '',
		location: form.location || '',
		contact: form.contact || '',
	};
}

/**
 * @param {Object} form
 * @returns {Object}
 */
export function webPutBody(form) {
	const raw = { ...(form._raw || {}) };
	return {
		...raw,
		httpsport: Number(form.httpsPort) || raw.httpsport || 443,
		realm: form.realm || '',
		httpredirect: form.redirectHttp ? 1 : 0,
	};
}

/** @type {Record<number, { key: string, label: string, tone: string }>} */
const EVENT_GROUPS = {
	4: { key: 'safety', label: 'Safety', tone: 'danger' },
	1: { key: 'detection', label: 'Detection', tone: 'warn' },
	8: { key: 'environment', label: 'Environment', tone: 'ok' },
};

/**
 * Map eventgen.events into console Events groups.
 * @param {Object} eventgen
 * @returns {{ configured: number, enabled: number, groups: Array, raw: Object }}
 */
export function mapEvents(eventgen) {
	const events = (eventgen && eventgen.events) || [];
	const byGroup = {};
	Object.values(EVENT_GROUPS).forEach((g) => {
		byGroup[g.key] = { ...g, items: [] };
	});
	const other = { key: 'other', label: 'Other', tone: 'ok', items: [] };
	events.forEach((ev) => {
		const meta = EVENT_GROUPS[ev.group] || null;
		const item = {
			id: ev.id,
			name: ev.id,
			source: ev.source || '',
			threshold: ev.threshold,
			enabled: !!ev.enabled,
			_raw: ev,
		};
		if (meta) byGroup[meta.key].items.push(item);
		else other.items.push(item);
	});
	const groups = Object.values(byGroup).filter((g) => g.items.length);
	if (other.items.length) groups.push(other);
	const enabled = events.filter((e) => e.enabled).length;
	return {
		configured: events.length,
		enabled,
		groups,
		raw: eventgen,
	};
}

/**
 * Apply UI event edits back onto eventgen for PUT.
 * @param {Object} eventgen
 * @param {Array} groups
 * @returns {Object}
 */
export function eventsPutBody(eventgen, groups) {
	const byId = {};
	(groups || []).forEach((g) => {
		(g.items || []).forEach((item) => {
			byId[item.id] = item;
		});
	});
	const next = JSON.parse(JSON.stringify(eventgen || { events: [] }));
	next.events = (next.events || []).map((ev) => {
		const ui = byId[ev.id];
		if (!ui) return ev;
		return {
			...ev,
			enabled: !!ui.enabled,
			threshold: ui.threshold === '' || ui.threshold == null
				? ev.threshold
				: Number(ui.threshold),
		};
	});
	return next;
}

/** Blocks that are diagnostics / not air readings. */
const READINGS_SKIP_BLOCKS = new Set([
	'devmonitor',
	'ethsw',
	'blebcn',
	'psel',
	'extin',
	'accsensor',
	'event_state',
]);

/**
 * Known numeric fields found under unknown / future sensor blocks.
 * @type {Record<string, {key:string,label:string,unit:string,icon:string}>}
 */
const GENERIC_FIELD_MAP = {
	ftemp: { key: 'temp', label: 'Temp', unit: '°F', icon: 'mdi-thermometer' },
	TF: { key: 'temp', label: 'Temp', unit: '°F', icon: 'mdi-thermometer' },
	humidity: { key: 'rh', label: 'Humidity', unit: '%', icon: 'mdi-water-percent' },
	RH: { key: 'rh', label: 'Humidity', unit: '%', icon: 'mdi-water-percent' },
	hum: { key: 'rh', label: 'Humidity', unit: '%', icon: 'mdi-water-percent' },
	co2: { key: 'co2', label: 'CO2', unit: 'ppm', icon: 'mdi-cloud-outline' },
	co2f: { key: 'co2', label: 'CO2', unit: 'ppm', icon: 'mdi-cloud-outline' },
	tvoc: { key: 'tvoc', label: 'TVOC', unit: 'ppb', icon: 'mdi-blur' },
	tvocf: { key: 'tvoc', label: 'TVOC', unit: 'ppb', icon: 'mdi-blur' },
	TVOC: { key: 'tvoc', label: 'TVOC', unit: 'ppb', icon: 'mdi-blur' },
	no2: { key: 'no2', label: 'NO2', unit: 'ppb', icon: 'mdi-atom' },
	co: { key: 'co', label: 'CO', unit: 'ppm', icon: 'mdi-molecule' },
	nh3: { key: 'nh3', label: 'NH3', unit: 'ppm', icon: 'mdi-flask-outline' },
	pm1: { key: 'pm1', label: 'PM1', unit: 'µg/m³', icon: 'mdi-blur-radial' },
	pm2p5: { key: 'pm25', label: 'PM2.5', unit: 'µg/m³', icon: 'mdi-blur-radial' },
	pm10: { key: 'pm10', label: 'PM10', unit: 'µg/m³', icon: 'mdi-blur-radial' },
	alux: { key: 'light', label: 'Light', unit: 'lux', icon: 'mdi-white-balance-sunny' },
	displux: { key: 'light', label: 'Light', unit: 'lux', icon: 'mdi-white-balance-sunny' },
	press: { key: 'pressure', label: 'Pressure', unit: 'inHg', icon: 'mdi-gauge' },
	millibar: { key: 'pressure-mb', label: 'Pressure', unit: 'mbar', icon: 'mdi-gauge' },
	EtOH: { key: 'etoh', label: 'EtOH', unit: '', icon: 'mdi-bottle-tonic' },
	IAQ: { key: 'iaq', label: 'IAQ', unit: '', icon: 'mdi-air-filter' },
};

/**
 * @param {*} block
 * @returns {Object}
 */
function sensorData(block) {
	if (!block || typeof block !== 'object') return {};
	if (block.data && typeof block.data === 'object' && !Array.isArray(block.data)) return block.data;
	return block;
}

/**
 * First non-null / non-empty candidate.
 * @param {...*} vals
 * @returns {*}
 */
function firstPresent(...vals) {
	for (const v of vals) {
		if (v != null && v !== '') return v;
	}
	return null;
}

/**
 * Build reading cards from whatever /latest returns for this Halo.
 * Only includes metrics that are present so different models show different cards.
 * @param {Object} latest
 * @returns {Array}
 */
export function mapReadings(latest) {
	const L = latest || {};
	const cards = [];
	const seen = new Set();
	const handled = new Set();

	/**
	 * @param {string} key
	 * @param {string} label
	 * @param {*} raw
	 * @param {string} unit
	 * @param {string} icon
	 * @param {Object} [extra]
	 */
	function add(key, label, raw, unit, icon, extra = {}) {
		if (raw == null || raw === '') return;
		if (seen.has(key)) return;
		const n = Number(raw);
		const numeric = Number.isFinite(n);
		if (!numeric && typeof raw !== 'string') return;
		seen.add(key);
		cards.push({
			key,
			label,
			value: numeric ? fmt(n) : String(raw),
			unit: unit || '',
			icon: icon || 'mdi-gauge',
			...extra,
		});
	}

	const ht = sensorData(L.htsensor);
	const gas = sensorData(L.gassensor);
	const co2 = sensorData(L.co2sensor);
	const aqiBrd = sensorData(L.aqibrd);
	const pm = sensorData(L.pmsensor);
	const lux = sensorData(L.luxsensor);
	const aud = sensorData(L.audsensor);
	const bar = sensorData(L.bar2);
	const nth = sensorData(L.nth);
	const pir = sensorData(L.pir);
	const hi = sensorData(L.HealthIndex);
	handled.add('htsensor');
	handled.add('gassensor');
	handled.add('co2sensor');
	handled.add('aqibrd');
	handled.add('pmsensor');
	handled.add('luxsensor');
	handled.add('audsensor');
	handled.add('bar2');
	handled.add('nth');
	handled.add('pir');
	handled.add('HealthIndex');
	handled.add('AQI');

	add('temp', 'Temp', firstPresent(ht.ftemp, nth.TF), '°F', 'mdi-thermometer');
	add('rh', 'Humidity', firstPresent(ht.humidity, aqiBrd.hum, nth.RH), '%', 'mdi-water-percent');
	add('co2', 'CO2', firstPresent(co2.co2, co2.co2f, aqiBrd.co2), 'ppm', 'mdi-cloud-outline');
	add('tvoc', 'TVOC', firstPresent(co2.tvoc, co2.tvocf, co2.TVOC), 'ppb', 'mdi-blur');

	const aqiObj = L.AQI || {};
	const aqiVal = firstPresent(
		aqiObj.value,
		aqiObj.data && aqiObj.data.value,
		aqiBrd.AQI,
		L.event_state && L.event_state.AQI && L.event_state.AQI.val
	);
	const aqiNum = aqiVal != null ? Number(aqiVal) : NaN;
	if (Number.isFinite(aqiNum)) {
		add('aqi', 'AQI', aqiNum, aqiObj.name || aqiObj.source || '', 'mdi-leaf', {
			good: aqiNum <= 50,
		});
	}

	add('no2', 'NO2', gas.no2, 'ppb', 'mdi-atom');
	add('co', 'CO', gas.co, 'ppm', 'mdi-molecule');
	add('nh3', 'NH3', gas.nh3, 'ppm', 'mdi-flask-outline');
	add('pm1', 'PM1', pm.pm1, 'µg/m³', 'mdi-blur-radial');
	add('pm25', 'PM2.5', firstPresent(pm.pm2p5, pm['pm2.5']), 'µg/m³', 'mdi-blur-radial');
	add('pm10', 'PM10', pm.pm10, 'µg/m³', 'mdi-blur-radial');
	add('light', 'Light', firstPresent(lux.displux, lux.alux), 'lux', 'mdi-white-balance-sunny');
	add('sound', 'Sound', firstPresent(aud.sum, aud.l), 'dB', 'mdi-volume-high');
	add('pressure', 'Pressure', firstPresent(ht.press, bar.press), 'inHg', 'mdi-gauge');
	add('etoh', 'EtOH', co2.EtOH, '', 'mdi-bottle-tonic');
	add('iaq', 'IAQ', co2.IAQ, '', 'mdi-air-filter');
	add('motion', 'Motion', firstPresent(pir.avg, pir.max), '', 'mdi-motion-sensor');

	if (hi.val != null && hi.val !== '') {
		const hiLabel = (Array.isArray(hi.clr) && hi.clr[0]) || '';
		add('health', 'Health Index', hi.val, hiLabel, 'mdi-heart-pulse', {
			good: Number(hi.val) <= 1,
		});
	}

	// External / NTH probe (only if distinct from primary HT)
	if (nth.TF != null && ht.ftemp == null) {
		add('nth-temp', 'NTH Temp', nth.TF, '°F', 'mdi-thermometer');
	} else if (nth.TF != null && Number(nth.TF) !== Number(ht.ftemp)) {
		add('nth-temp', 'NTH Temp', nth.TF, '°F', 'mdi-thermometer');
	}
	if (nth.RH != null && ht.humidity == null) {
		add('nth-rh', 'NTH Humidity', nth.RH, '%', 'mdi-water-percent');
	} else if (nth.RH != null && Number(nth.RH) !== Number(ht.humidity)) {
		add('nth-rh', 'NTH Humidity', nth.RH, '%', 'mdi-water-percent');
	}

	// Future / model-specific blocks: map only known metric field names
	Object.keys(L).forEach((blockKey) => {
		if (handled.has(blockKey) || READINGS_SKIP_BLOCKS.has(blockKey)) return;
		const data = sensorData(L[blockKey]);
		Object.keys(data).forEach((field) => {
			const spec = GENERIC_FIELD_MAP[field];
			if (!spec) return;
			add(spec.key, spec.label, data[field], spec.unit, spec.icon);
		});
	});

	return cards;
}

/**
 * @param {*} v
 * @returns {string}
 */
function fmt(v) {
	if (v == null || v === '') return '—';
	const n = Number(v);
	if (!Number.isFinite(n)) return String(v);
	return Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10);
}

/**
 * Format uptime from install epoch ms.
 * @param {number} installMs
 * @returns {string}
 */
export function formatUptime(installMs) {
	const t = Number(installMs);
	if (!Number.isFinite(t) || t <= 0) return '—';
	const sec = Math.max(0, (Date.now() - t) / 1000);
	const days = Math.floor(sec / 86400);
	if (days >= 60) return `${(days / 30.4).toFixed(1)} mo`;
	if (days >= 2) return `${days} d`;
	const hrs = Math.floor(sec / 3600);
	if (hrs >= 2) return `${hrs} h`;
	return `${Math.floor(sec / 60)} min`;
}

/**
 * Whether a slice key was provided in this apply call.
 * @param {Object} slices
 * @param {string} key
 * @returns {boolean}
 */
function hasSlice(slices, key) {
	return Object.prototype.hasOwnProperty.call(slices || {}, key);
}

/**
 * Merge live gstate slices into console state (supports partial / progressive updates).
 * @param {Object} state
 * @param {Object} slices
 * @param {Object} edgeDevice
 * @returns {Object}
 */
export function applyLiveSlices(state, slices, edgeDevice) {
	const next = state;
	const prev = next._raw || {};
	const versions = hasSlice(slices, 'versions') ? (slices.versions || {}) : (prev.versions || {});
	const config = hasSlice(slices, 'config') ? (slices.config || {}) : (prev.config || {});
	const cloud = hasSlice(slices, 'cloud') ? (slices.cloud || {}) : (prev.cloud || {});
	const https = hasSlice(slices, 'https') ? (slices.https || {}) : (prev.https || {});
	const latest = hasSlice(slices, 'latest') ? (slices.latest || {}) : (prev.latest || {});
	const eventState = hasSlice(slices, 'eventState') ? (slices.eventState || {}) : (prev.eventState || {});
	const ident = config.ident || {};
	const edge = edgeDevice || {};

	next.online = true;

	if (hasSlice(slices, 'cloud') && cloud.domain) {
		next.tenant = cloud.domain;
	}

	if (hasSlice(slices, 'config') || hasSlice(slices, 'versions')) {
		if (config.devicename) next.device.name = config.devicename;
		else if (edge.name && edge.name !== '—') next.device.name = edge.name;
		if (versions.name) next.device.model = versions.name;
		if (versions.ver) next.device.firmware = versions.ver;
		if (versions.ser) next.device.serial = versions.ser;
		if (versions.install) next.device.uptime = formatUptime(versions.install);
		const eth = versions.ni && versions.ni.eth0 && versions.ni.eth0[0];
		if (eth) {
			if (eth.address) next.device.ip = eth.address;
			if (eth.mac) next.device.mac = eth.mac;
			if (eth.netmask) next.device.subnet = eth.netmask;
		}
		if (hasSlice(slices, 'config')) {
			next.device.location = formatIdentLocation(ident);
			next.identity = {
				name: config.devicename || next.device.name || '',
				wing: ident.wing != null ? String(ident.wing) : '',
				floor: ident.floor != null ? String(ident.floor) : '',
				room: ident.room != null ? String(ident.room) : '',
				st_addr: ident.st_addr != null ? String(ident.st_addr) : '',
				desc: ident.desc != null ? String(ident.desc) : '',
				latitude: ident.latitude != null ? String(ident.latitude) : '',
				longitude: ident.longitude != null ? String(ident.longitude) : '',
				student_count: ident.student_count != null ? String(ident.student_count) : '',
				_raw: ident,
			};
		}
	}

	if (hasSlice(slices, 'presets')) {
		const names = mapPresets(slices.presets);
		next.presets = {
			...(next.presets || {}),
			names,
			selected: (next.presets && next.presets.selected) || names[0] || '',
			mode: (next.presets && next.presets.mode) || 'Overwrite',
		};
	}

	if (hasSlice(slices, 'timeok')) {
		next.device.clockInSync = slices.timeok === 1 || slices.timeok === true || slices.timeok === '1';
	}

	if (hasSlice(slices, 'cloud')) {
		next.health.cloud = {
			status: /connect/i.test(String(cloud.state || '')) ? 'ok' : 'warn',
			title: cloud.state || 'Unknown',
			detail: cloud.domain || '',
		};
	}

	if (hasSlice(slices, 'config') || hasSlice(slices, 'https') || hasSlice(slices, 'vms') || hasSlice(slices, 'heartbeat')) {
		next.integrations.primary = mapIntegration(config.vms || slices.vms, 'primary', https);
		next.integrations.secondary = mapIntegration(config.vms2 || slices.vms2, 'secondary', https);
		next.integrations.heartbeat = mapIntegration(config.heartbeat || slices.heartbeat, 'heartbeat', https);
		next.health.heartbeat = {
			status: next.integrations.heartbeat.status,
			title: next.integrations.heartbeat.statusLabel,
			detail: next.integrations.heartbeat.error || next.integrations.heartbeat.host || '',
		};
		next.health.primary = {
			status: next.integrations.primary.status,
			title: next.integrations.primary.statusLabel,
			detail: next.integrations.primary.host || '',
		};
	}

	const eg = hasSlice(slices, 'eventgen')
		? slices.eventgen
		: (hasSlice(slices, 'config') ? config.eventgen : prev.eventgen);
	if (eg) next.events = mapEvents(eg);

	if (hasSlice(slices, 'eventState') || hasSlice(slices, 'config') || eg) {
		const activeEvents = Object.keys(eventState || {}).filter(
			(k) => k !== '$info$' && eventState[k] && eventState[k].didhappen
		);
		next.health.events = {
			status: 'ok',
			title: activeEvents.length ? `${activeEvents.length} active` : 'None active',
			detail: `${next.events.configured} configured`,
		};
	}

	if (hasSlice(slices, 'latest') && latest && Object.keys(latest).length) {
		next.readings = mapReadings(latest);
		const ethsw = (latest.ethsw && latest.ethsw.data) || latest.ethsw || {};
		if (ethsw.upspd != null && ethsw.upspd !== '') {
			next.device.link = `${ethsw.upspd} Mbps`;
		}
	}

	if (hasSlice(slices, 'config') || hasSlice(slices, 'mics') || hasSlice(slices, 'htsensor') || hasSlice(slices, 'blebcn')) {
		const mics = (config.sensor_config && config.sensor_config.mics) || slices.mics || {};
		const ht = (config.sensor_config && config.sensor_config.htsensor) || slices.htsensor || {};
		const ble = (config.drivers && config.drivers.blebcn && config.drivers.blebcn.config)
			|| slices.blebcn
			|| {};
		if (config.volume != null) next.sensors.volumeDb = String(config.volume);
		if (mics.levelL != null) next.sensors.micLevelL = String(mics.levelL);
		if (mics.levelR != null) next.sensors.micLevelR = String(mics.levelR);
		if (mics.gainL != null) next.sensors.micGainL = String(mics.gainL);
		if (mics.gainR != null) next.sensors.micGainR = String(mics.gainR);
		if (mics.dboffset != null) next.sensors.micDbOffset = String(mics.dboffset);
		if (ht.useroffset != null) next.sensors.tempOffsetC = String(ht.useroffset);
		next.sensors.amplifyEnabled = !!ble.enabled;
		const beacons = ble.beacons || [];
		next.sensors.amplifyNote = beacons.length
			? `${beacons.length} beacon(s) paired.`
			: 'No beacons paired.';
	}

	if (hasSlice(slices, 'config') || hasSlice(slices, 'bacnet') || hasSlice(slices, 'snmp') || hasSlice(slices, 'api')) {
		const bacnet = config.bacnet || slices.bacnet || {};
		next.advanced.bacnet = {
			instance: bacnet.instanceNumber != null ? String(bacnet.instanceNumber) : '',
			port: bacnet.port != null ? String(bacnet.port) : '',
			deviceName: bacnet.deviceName || '',
			covIncrement: bacnet.covIncrement != null ? String(bacnet.covIncrement) : '',
			location: bacnet.location != null ? String(bacnet.location) : '',
			foreignDevice: !!bacnet.foreignDevice,
			foreignBBMD: bacnet.foreignBBMD != null ? String(bacnet.foreignBBMD) : '',
			foreignTTL: bacnet.foreignTTL != null ? String(bacnet.foreignTTL) : '30',
			_raw: bacnet,
		};
		const snmp = config.snmp || slices.snmp || {};
		next.advanced.snmp = {
			enabled: !!(snmp.on || snmp.ena),
			community: snmp.community != null ? String(snmp.community) : '',
			username: snmp.username != null ? String(snmp.username) : '',
			rocommunity: snmp.rocommunity != null ? String(snmp.rocommunity) : '',
			rousername: snmp.rousername != null ? String(snmp.rousername) : '',
			location: snmp.location != null ? String(snmp.location) : '',
			contact: snmp.contact != null ? String(snmp.contact) : '',
			_raw: snmp,
		};
		const api = config.api || slices.api || {};
		next.advanced.web = {
			httpsPort: api.httpsport != null ? String(api.httpsport) : '',
			realm: api.realm || '',
			redirectHttp: api.httpredirect === 1 || api.httpredirect === true,
			_raw: api,
		};
	}

	next._raw = {
		...prev,
		versions,
		config,
		cloud,
		https,
		latest,
		eventState,
		eventgen: eg || prev.eventgen,
	};
	return next;
}

/**
 * Short locate LED pulse body (safe for lab).
 * @returns {Object}
 */
export function locateForceBody() {
	return {
		ledclr: 255,
		ledpat: 1,
		sound: '',
		secs: 3,
	};
}
